import { useEffect, useMemo, useState } from 'react';
import { IconRegistry } from '@tylertech/forge';
import {
  ForgeButton,
  ForgeDialog,
  ForgeIcon,
  ForgeScaffold,
  ForgeStack,
  ForgeTextField,
  ForgeToolbar
} from '@tylertech/forge-react';
import { tylIconCheckCircle, tylIconCheckboxBlankCircle } from '@tylertech/tyler-icons';
import { getPasswordPolicy, updatePassword } from '../../api/security';

const DEFAULT_SPECIAL_CHARACTERS = "!@_,.~`?^%+=-)(;':|}[]*^\\,~#";
const defaultPolicy = {
  minLength: 8,
  minUpperCase: 1,
  minLowerCase: 1,
  minNumber: 1,
  minSymbol: 0,
  excludeUsername: false,
  historyCount: 0
};

const buildRequirementsFromPolicy = policy => {
  const requirements = [];
  const minLength = Number(policy?.minLength) || defaultPolicy.minLength;
  const minUpperCase = Number(policy?.minUpperCase ?? defaultPolicy.minUpperCase);
  const minLowerCase = Number(policy?.minLowerCase ?? defaultPolicy.minLowerCase);
  const minNumber = Number(policy?.minNumber ?? defaultPolicy.minNumber);
  const minSymbol = Number(policy?.minSymbol ?? defaultPolicy.minSymbol);

  requirements.push({
    label: `Minimum ${minLength} characters long`,
    check: value => value.length >= minLength
  });

  if (minUpperCase > 0) {
    const label = minUpperCase > 1 ? `At least ${minUpperCase} uppercase letters` : 'Uppercase letter';
    requirements.push({ label, check: value => (value.match(/[A-Z]/g) || []).length >= minUpperCase });
  }

  if (minLowerCase > 0) {
    const label = minLowerCase > 1 ? `At least ${minLowerCase} lowercase letters` : 'Lowercase letter';
    requirements.push({ label, check: value => (value.match(/[a-z]/g) || []).length >= minLowerCase });
  }

  if (minNumber > 0) {
    const label = minNumber > 1 ? `At least ${minNumber} digits (0-9)` : 'One digit (0-9)';
    requirements.push({ label, check: value => (value.match(/\d/g) || []).length >= minNumber });
  }

  if (minSymbol > 0) {
    const label = minSymbol > 1 ? `At least ${minSymbol} special characters` : 'Special character';
    requirements.push({
      label,
      check: value => (value.match(/[^A-Za-z0-9]/g) || []).length >= minSymbol
    });
  }

  if (policy?.excludeUsername) {
    requirements.push({
      label: 'Must not contain your username',
      check: () => true // Cannot validate client-side without knowing the username
    });
  }

  if (Number(policy?.historyCount) > 0) {
    requirements.push({
      label: `Must not match your last ${policy.historyCount} passwords`,
      check: () => true // Cannot validate client-side; enforced server-side
    });
  }

  return requirements;
};

const DEFAULT_REQUIREMENTS = buildRequirementsFromPolicy(defaultPolicy);

export default function ChangePasswordDialog({ open, onClose, onSave }) {
  IconRegistry.define([tylIconCheckCircle, tylIconCheckboxBlankCircle]);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [requirements, setRequirements] = useState(DEFAULT_REQUIREMENTS);
  const [currentPasswordError, setCurrentPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');

  useEffect(() => {
    if (open) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setIsSaving(false);
      setRequirements(DEFAULT_REQUIREMENTS);
      setCurrentPasswordError('');
      setConfirmPasswordError('');

      let isMounted = true;
      const loadPolicy = async () => {
        try {
          const policy = await getPasswordPolicy();
          if (isMounted) {
            const policyRequirements = buildRequirementsFromPolicy(policy);
            setRequirements(policyRequirements.length ? policyRequirements : DEFAULT_REQUIREMENTS);
          }
        } catch (_err) {
          // If policy fetch fails, continue using defaults
          if (isMounted) {
            setRequirements(DEFAULT_REQUIREMENTS);
          }
        }
      };

      loadPolicy();
      return () => {
        isMounted = false;
      };
    }
  }, [open]);

  const passwordRequirements = useMemo(
    () =>
      requirements.map(req => ({
        label: req.label,
        satisfied: req.check(newPassword)
      })),
    [newPassword, requirements]
  );

  const allRequirementsMet = passwordRequirements.every(req => req.satisfied);
  const passwordsMatch = !confirmPassword || newPassword === confirmPassword;
  const canSubmit = Boolean(
    currentPassword && newPassword && confirmPassword && passwordsMatch && allRequirementsMet && !isSaving
  );

  // Real-time validation for confirm password
  useEffect(() => {
    if (confirmPassword) {
      if (!passwordsMatch) {
        setConfirmPasswordError('Passwords do not match');
      } else {
        setConfirmPasswordError('');
      }
    } else {
      setConfirmPasswordError('');
    }
  }, [confirmPassword, passwordsMatch]);

  const handleSubmit = async event => {
    event?.preventDefault?.();
    if (isSaving) return;

    setError('');
    setSuccess('');
    setCurrentPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('All password fields are required.');
      return;
    }

    if (!allRequirementsMet) {
      setError('New password does not meet the requirements.');
      return;
    }

    setIsSaving(true);
    try {
      await updatePassword({ currentPassword, newPassword });
      setSuccess('Password updated successfully.');
      if (typeof onSave === 'function') {
        onSave();
      }
    } catch (err) {
      // Handle different types of errors more specifically
      const errorMessage = err?.response?.data?.message || err?.message;
      
      if (errorMessage && errorMessage.toLowerCase().includes('current password')) {
        setCurrentPasswordError('Current password is incorrect');
        setError('Current password is incorrect. Please try again.');
      } else if (errorMessage && errorMessage.toLowerCase().includes('password')) {
        setError(errorMessage);
      } else {
        setError('Unable to update your password right now. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ForgeDialog
      className="dialog--large"
      open={open}
      on-forge-dialog-close={onClose}
      label="Change Password"
      description="Update your account password">
      <ForgeScaffold>
        <ForgeToolbar slot="header" className="dialog-header">
          <h2 slot="start" className="forge-typography--subheading6">
            Change Password
          </h2>
        </ForgeToolbar>
        <form slot="body" className="dialog-body" onSubmit={handleSubmit}>
          <ForgeStack>
            {error ? <p className="dialog-status dialog-status--error">{error}</p> : null}
            {success ? <p className="dialog-status dialog-status--success">{success}</p> : null}
            <p className="forge-typography--subheading2 text--bold">Password Requirements</p>
            <ul className="flex-col gap-2 ml-4 mb-2 list-style-none text-color--medium forge-typography--body2" role="list" id="password-requirements">
              {passwordRequirements.map(req => (
                <li className="password-requirements__item flex-row-reverse gap-2" key={req.label} role="listitem">
                  {/* The label is placed before the icon to read better with a screen reader, the visual order is reversed with CSS */}
                  <span>{req.label}</span>
                  <ForgeIcon
                    className={req.satisfied ? 'text-color--success' : 'text-color--low'}
                    name={req.satisfied ? 'check_circle' : 'checkbox_blank_circle'}
                    role="img"
                    aria-label={req.satisfied ? 'satisfied' : 'not satisfied'}
                  />
                </li>
              ))}
            </ul>
            <ForgeTextField invalid={!!currentPasswordError}>
              <label slot="label">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onInput={evt => {
                  setCurrentPassword(evt.target.value);
                  // Clear current password error when user starts typing
                  if (currentPasswordError) {
                    setCurrentPasswordError('');
                  }
                }}
                autoComplete="current-password"
                required
                disabled={isSaving}
                aria-invalid={!!currentPasswordError}
                aria-describedby={currentPasswordError ? "current-password-error" : undefined}
              />
              {currentPasswordError && (
                <div id="current-password-error" slot="helper-text" className="text-color--error">
                  {currentPasswordError}
                </div>
              )}
            </ForgeTextField>
            <ForgeTextField>
              <label slot="label">New Password</label>
              <input
                type="password"
                value={newPassword}
                onInput={evt => setNewPassword(evt.target.value)}
                aria-describedby="password-requirements"
                autoComplete="new-password"
                required
                disabled={isSaving}
              />
            </ForgeTextField>
            <ForgeTextField invalid={!!confirmPasswordError}>
              <label slot="label">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onInput={evt => setConfirmPassword(evt.target.value)}
                autoComplete="new-password"
                aria-invalid={!!confirmPasswordError}
                aria-describedby={confirmPasswordError ? "confirm-password-error" : undefined}
                required
                disabled={isSaving}
              />
              {confirmPasswordError && (
                <div id="confirm-password-error" slot="helper-text" className="text-color--error">
                  {confirmPasswordError}
                </div>
              )}
            </ForgeTextField>
          </ForgeStack>
        </form>
        <ForgeToolbar slot="footer" inverted>
          <ForgeStack slot="end" inline>
            <ForgeButton variant="outlined" onClick={onClose} disabled={isSaving}>
              Cancel
            </ForgeButton>
            <ForgeButton variant="filled" type="button" disabled={!canSubmit} onClick={handleSubmit}>
              {isSaving ? 'Saving...' : 'Save'}
            </ForgeButton>
          </ForgeStack>
        </ForgeToolbar>
      </ForgeScaffold>
    </ForgeDialog>
  );
}
